import styled, { css } from 'styled-components'

export interface TriggerButtonProps {
  $disabled?: boolean
  $error?: boolean
  $open?: boolean
  $width?: string | number
  $size?: 'small' | 'medium' | 'large'
}

const sizeStyles = {
  small: css`
    height: 28px;
    padding: 0 ${({ theme }) => theme.spacing.small};
    font-size: ${({ theme }) => theme.typography.fontSize.micro};

    /* Mobile: Ensure 44px minimum touch target */
    @media (max-width: 1023px) {
      height: ${({ theme }) => theme.touchTargets.minimum};
    }
  `,
  medium: css`
    height: ${({ theme }) => theme.components.input.height};
    padding: ${({ theme }) => theme.components.input.padding};
    font-size: ${({ theme }) => theme.typography.fontSize.small};

    /* Mobile: Ensure 44px minimum touch target */
    @media (max-width: 1023px) {
      height: ${({ theme }) => theme.touchTargets.minimum};
    }
  `,
  large: css`
    height: 40px;
    padding: 0 ${({ theme }) => theme.spacing.default};
    font-size: ${({ theme }) => theme.typography.fontSize.body};

    /* Mobile: Ensure 44px minimum touch target */
    @media (max-width: 1023px) {
      height: ${({ theme }) => theme.touchTargets.comfortable};
    }
  `,
}

export const TriggerButton = styled.button<TriggerButtonProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  text-align: left;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  gap: ${({ theme }) => theme.spacing.small};

  ${({ $size = 'medium' }) => sizeStyles[$size]}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.focus};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.background.tertiary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    border-color: ${({ theme }) => theme.colors.border.light};
  }

  ${({ $error, theme }) =>
    $error &&
    css`
      border-color: ${theme.colors.border.error};
      box-shadow: ${theme.shadows.error};
    `}

  ${({ $open, theme }) =>
    $open &&
    css`
      border-color: ${theme.colors.border.focus};
      box-shadow: ${theme.shadows.focus};
    `}
`

export const Content = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  flex-shrink: 0;
`

export const Icon = styled.span<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: ${({ theme }) => theme.transitions.default};
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: 1;
`
